import { SCHEMA } from '@vedalib/editor/lib/brand'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { useCallback, useMemo } from 'react'
import React from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import {
  FieldColorPicker,
  FieldFileId,
  FieldNumber,
  FieldSegmented,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { TileButton } from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { BrandFieldShadow, BrandTagEdited } from 'services/Branding/components'
import { BORDER_OPTIONS } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type { TestHotspotFormBrandType } from './testHotspotTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.testHotspot.style, {
  image: {
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  area: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
  },
  spot: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('input.label.spotImage'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.pulsationColor'),
      useConfigs: [useBrandColorOptions],
    }),
    height: genBrandField({
      as: FieldNumber,
      label: t('input.label.height'),
      rules: [{ min: 0, message: ' ' }],
      props: { width: 64 },
    }),
    width: genBrandField({
      as: FieldNumber,
      label: t('input.label.width'),
      rules: [{ min: 0, message: ' ' }],
      props: { width: 64 },
    }),
  },
  selectedSpot: {
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.iconColor'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  hover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.testHotspot.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
  animation: {
    pulse: genBrandField({
      as: FieldSegmented<boolean>,
      label: t('input.label.spotPulse'),
      props: { fluid: false },
    }),
  },
})

const TestHotspotFormStyle: TestHotspotFormBrandType = ({
  device,
  subConfigs,
  edited,
  setForm,
}) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const handleSetFeedback = useCallback(() => setForm?.(BrandFormEnum.FEEDBACK), [setForm])

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom bottomSpace gap sides>
        <Typography weight='medium'>{t('elements.testHotspot.tags.image')}</Typography>
        <FormField config={schema.image.borderRadius} />
        <FormField config={schema.image.borderStyle} />
        <FormField config={schema.image.borderColor} />
        <FormField config={schema.image.borderWidth} />

        <Typography weight='medium'>{t('elements.testHotspot.tags.area')}</Typography>
        <FormField config={schema.area.backgroundColor} />
        <FormField config={schema.area.borderColor} />

        <Typography weight='medium'>{t('elements.testHotspot.tags.spot')}</Typography>
        <FormField config={schema.spot.backgroundImage} />
        <FormField config={schema.spot.backgroundColor} />
        <FormField config={schema.selectedSpot.color} />
        <FormField config={schema.spot.width} />
        <FormField config={schema.spot.height} />
        <FormField config={schema.hover.backgroundColor} />
        <FormField config={schema.animation.pulse} />
        <FormField config={schema.spot.color} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />

        <TileButton
          description={t('uiKit.button.feedbackDescription')}
          icon='otherRight'
          name='showFeedbackForm'
          onClick={handleSetFeedback}
        >
          <Typography weight='medium'>{t('page.brand.tab.feedback')}</Typography>
          {isAnyEdited(edited.feedback) && <BrandTagEdited />}
        </TileButton>

        <TileButton
          description={t('uiKit.button.richTextDescription')}
          icon='otherRight'
          name='showFontForm'
          onClick={handleSetTypography}
        >
          <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
          {isAnyEdited(edited.font) && <BrandTagEdited />}
        </TileButton>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(TestHotspotFormStyle) as typeof TestHotspotFormStyle
